import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import FileUploader from "../components/FileUploader";
import FileList from "../components/FileList";
import { ArrowLeft, Send, Shield, Paperclip } from "lucide-react";
import { toast } from "sonner";

export default function VendorQuote() {
  const { srfqId } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [prices, setPrices] = useState({});
  const [unitSystem, setUnitSystem] = useState("Imperial");
  const [hseScore, setHseScore] = useState(80);
  const [materials, setMaterials] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedQuoteId, setSubmittedQuoteId] = useState(null);
  const [certRefresh, setCertRefresh] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sub-rfqs/public/${srfqId}`);
        setSub(data);
      } catch { toast.error("Sub-RFQ not found"); navigate("/vendor"); }
    })();
  }, [srfqId, navigate]);

  const total = Object.values(prices).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const submit = async () => {
    if (!sub) return;
    setSubmitting(true);
    try {
      const linePrices = sub.sub_rfq.line_items.map(li => ({
        line_id: li.id,
        description: li.description,
        quantity: li.quantity,
        unit: li.unit,
        unit_price: parseFloat(prices[li.id]) || 0,
        total: (parseFloat(prices[li.id]) || 0) * li.quantity,
        offered_spec: li.spec,
      }));
      const { data } = await api.post("/quotes/submit", {
        sub_rfq_id: srfqId,
        line_prices: linePrices,
        total_amount: total * (sub.sub_rfq.line_items[0]?.quantity || 1),
        currency: "USD",
        unit_system: unitSystem,
        hse_score: parseInt(hseScore),
        materials: materials.split(",").map(s => s.trim()).filter(Boolean),
      });
      setSubmittedQuoteId(data.id);
      toast.success("Quote submitted · attach files below or return to portal");
    } catch { toast.error("Submit failed"); }
    finally { setSubmitting(false); }
  };

  if (!sub) return <Layout><div className="py-20 text-center font-mono-data text-xs animate-pulse">Loading bid...</div></Layout>;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="vendor-quote-page">
        <button onClick={() => navigate("/vendor")} data-testid="vendor-back-btn" className="font-mono-data text-xs uppercase tracking-widest text-text-secondary hover:text-white inline-flex items-center gap-2 mb-6">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>

        <div className="tray p-6 mb-6">
          <div className="font-mono-data text-[10px] tracking-widest uppercase text-amber-500 mb-2">
            {sub.sub_rfq.category} Sub-RFQ
          </div>
          <h1 className="font-display text-3xl mb-1">{sub.rfp_title}</h1>
          <div className="font-mono-data text-xs text-text-secondary">{sub.operator} · Due {sub.deadline}</div>
        </div>

        <div className="space-y-4 mb-6">
          {sub.sub_rfq.line_items.map(li => (
            <div key={li.id} className="tray-inset p-4" data-testid={`line-input-${li.id}`}>
              <div className="font-display text-base mb-1">{li.description}</div>
              <div className="font-mono-data text-[10px] text-text-secondary uppercase tracking-widest mb-3">
                {li.quantity} {li.unit} · {li.spec}
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono-data text-xs text-text-secondary">Unit Price</span>
                <input
                  type="number"
                  value={prices[li.id] || ""}
                  onChange={e => setPrices({ ...prices, [li.id]: e.target.value })}
                  placeholder="0.00"
                  data-testid={`price-input-${li.id}`}
                  className="flex-1 bg-transparent border-b border-white/10 px-2 py-1 font-mono-data text-base outline-none focus:border-amber-500"
                />
                <span className="font-mono-data text-xs text-text-secondary">USD / {li.unit}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="tray p-5 mb-6 space-y-4">
          <div>
            <label className="block font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-2">Unit System</label>
            <div className="flex gap-2">
              {["Imperial", "Metric"].map(s => (
                <button key={s} onClick={() => setUnitSystem(s)} data-testid={`unit-${s}-btn`}
                  className={`tray-inset flex-1 py-2 font-mono-data text-xs uppercase ${unitSystem === s ? "ring-2 ring-amber-500 text-amber-500" : "text-text-secondary"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-2 inline-flex items-center gap-2">
              <Shield className="w-3 h-3 text-amber-500" /> HSE Safety Score (self-attested)
            </label>
            <input
              type="number" min="0" max="100"
              value={hseScore}
              onChange={e => setHseScore(e.target.value)}
              data-testid="hse-score-input"
              className="w-full tray-inset px-3 py-2 bg-transparent font-mono-data text-sm outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>
          <div>
            <label className="block font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-2">Materials / Specs Offered (comma sep)</label>
            <input
              value={materials}
              onChange={e => setMaterials(e.target.value)}
              data-testid="materials-input"
              placeholder="Duplex 2205, API 5L X65, ASTM A182 F51"
              className="w-full tray-inset px-3 py-2 bg-transparent font-mono-data text-sm outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>
        </div>

        <div className="tray p-5 mb-6">
          <div className="flex justify-between items-baseline">
            <span className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary">Quote Total</span>
            <span className="font-display text-3xl text-amber-500">${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        <button onClick={submit} disabled={submitting || submittedQuoteId} data-testid="submit-quote-btn"
          className="btn-tactile btn-amber w-full inline-flex items-center justify-center gap-2">
          <Send className="w-4 h-4" /> {submitting ? "Submitting..." : submittedQuoteId ? "Quote Submitted ✓" : "Submit Quote"}
        </button>

        {submittedQuoteId && (
          <>
            <div className="tray p-5 mt-6" data-testid="attach-quote-docs">
              <div className="flex items-center gap-2 mb-3">
                <Paperclip className="w-4 h-4 text-amber-500" />
                <div>
                  <div className="font-mono-data text-[10px] tracking-widest uppercase text-amber-500">Optional</div>
                  <div className="font-display text-lg">Attach Quote Documents</div>
                  <div className="text-xs text-text-secondary mt-0.5">Cut sheets, spec sheets, cost breakdown PDFs</div>
                </div>
              </div>
              <FileUploader scope="vendor_quote_attachment" scopeId={submittedQuoteId} label="Drop quote docs" />
              <div className="mt-4">
                <FileList scope="vendor_quote_attachment" scopeId={submittedQuoteId} emptyLabel="No attachments yet" />
              </div>
            </div>

            <div className="tray p-5 mt-4" data-testid="upload-hse-cert">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-amber-500" />
                <div>
                  <div className="font-mono-data text-[10px] tracking-widest uppercase text-amber-500">HSE Certifications</div>
                  <div className="font-display text-lg">Upload Safety Certificates</div>
                  <div className="text-xs text-text-secondary mt-0.5">OSHA, API, NACE, OPITO — with expiry tracking</div>
                </div>
              </div>
              <FileUploader
                scope="hse_cert"
                showCertFields
                label="Drop certificate PDF"
                onUploaded={() => setCertRefresh(k => k + 1)}
              />
              <div className="mt-4">
                <FileList scope="hse_cert" showCert refreshKey={certRefresh} emptyLabel="No certificates on file" />
              </div>
            </div>

            <button onClick={() => navigate("/vendor")} data-testid="done-btn"
              className="btn-tactile w-full mt-4">
              Done · Back to Portal
            </button>
          </>
        )}
      </div>
    </Layout>
  );
}
